'use client';

import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { useCustomAddressStore } from '@libs/common-next';
import type { Product, TailoredApiData } from '@libs/common-types';

import {
    type DeliveryInventoryStatus,
    getBopisFulfillmentStatus,
    getDeliveryInventoryStatus,
    getSddFulfillmentStatus,
    getSthFulfillmentStatus,
    resolveFulfillmentMethod,
    type SddDetailsProps,
} from '../../product-details';
import { isMethodAvailableForPurchaseOption } from '../lib';
import type { MiniPdpForm } from '../types';

export type MiniPdpFulfillmentProps = Readonly<{
    product: Product;
    tailored: TailoredApiData | null;
    isLoading: boolean;
    isError?: boolean;
    onOpenSddAddress?: () => void;
    onOpenStoreDrawer: () => void;
}>;

export function useMiniPdpFulfillment({
    product,
    tailored,
    isLoading,
    isError = false,
    onOpenSddAddress,
}: Omit<MiniPdpFulfillmentProps, 'onOpenStoreDrawer'>) {
    const { register, setValue } = useFormContext<MiniPdpForm>();
    const selectedMethod = useWatch({ name: 'fulfilmentMethod' });
    const selectedSku = useWatch({ name: 'selectedSku' });
    const purchaseOption = useWatch({ name: 'purchaseOption' });
    const customAddress = useCustomAddressStore(state => state.address);

    const tailoredVariant = selectedSku ? tailored?.variants[selectedSku] : undefined;
    const bopis = tailoredVariant?.BOPIS;
    const sdd = tailoredVariant?.SDD;
    const sth = tailoredVariant?.STH;

    const isBopisAvailableForPurchase = isMethodAvailableForPurchaseOption(product, 'BOPIS', purchaseOption);
    const isBopisAvailableForStore = bopis?.storeId
        ? (tailored?.bopisStoreData?.[bopis.storeId]?.offersBopis ?? true)
        : true;
    const isBopisAvailable = isBopisAvailableForPurchase && isBopisAvailableForStore;
    const isSddAvailable = isMethodAvailableForPurchaseOption(product, 'SDD', purchaseOption);
    const isSthAvailable = isMethodAvailableForPurchaseOption(product, 'STH', purchaseOption);

    const availableMethods = {
        bopis: isBopisAvailable,
        sdd: isSddAvailable,
        sth: isSthAvailable,
    };

    useEffect(() => {
        if (isLoading) return;

        const resolvedMethod = resolveFulfillmentMethod({
            availableMethods,
            selectedMethod,
        });

        if (resolvedMethod !== selectedMethod) {
            setValue('fulfilmentMethod', resolvedMethod);
        }
    }, [isLoading, selectedMethod, setValue, isBopisAvailable, isSddAvailable, isSthAvailable]);

    const fulfillmentMethods = [
        {
            value: 'bopis' as const,
            title: 'Pick up in store',
            iconName: 'store-outline' as const,
            statusTestId: 'bopis-tile-status-message',
            status: getBopisFulfillmentStatus({
                availableStock: bopis?.availableStock ?? 0,
                altAvailableStock: bopis?.altAvailableStock ?? 0,
                minimumCaseQty: product.deliveryMethods.BOPIS.purchaseQuantities?.min ?? 1,
                isStoreSelected: Boolean(bopis?.storeId),
                isPharmacy: Boolean(product.pharmacy),
            }),
            isSelected: selectedMethod === 'bopis' && !isLoading && isBopisAvailable,
            isDisabled: !isLoading && !isBopisAvailable,
            isLoading,
        },
        {
            value: 'sdd' as const,
            title: 'Same-day delivery',
            iconName: 'box-outline' as const,
            statusTestId: 'sdd-tile-status-message',
            status: getSddFulfillmentStatus({
                availableStock: sdd?.availableStock ?? 0,
                minimumCaseQty: product.deliveryMethods.SDD.purchaseQuantities?.min ?? 1,
                isAvailable: Boolean(sdd?.doorDashValidationSuccess),
                hasStreetAddress: Boolean(customAddress?.streetLine1),
            }),
            isSelected: selectedMethod === 'sdd' && !isLoading && isSddAvailable,
            isDisabled: !isLoading && !isSddAvailable,
            isLoading,
        },
        {
            value: 'sth' as const,
            title: 'Ship to me',
            iconName: 'car-outline' as const,
            statusTestId: 'stm-tile-status-message',
            status: getSthFulfillmentStatus({
                availableStock: sth?.availableStock ?? 0,
                minimumCaseQty: product.deliveryMethods.STH.purchaseQuantities?.min ?? 1,
                isPharmacyOutOfStock: Boolean(product.pharmacy?.oos),
            }),
            isSelected: selectedMethod === 'sth' && !isLoading && isSthAvailable,
            isDisabled: !isLoading && !isSthAvailable,
            isLoading,
        },
    ];

    const sddDetails: SddDetailsProps | null =
        selectedMethod === 'sdd' && !isError
            ? {
                  isLoading,
                  streetAddress: customAddress?.streetLine1,
                  availableStock: sdd?.availableStock ?? 0,
                  minimumCaseQty: product.deliveryMethods.SDD.purchaseQuantities?.min ?? 1,
                  isDoorDashAvailable: Boolean(sdd?.doorDashValidationSuccess),
                  onOpenAddressDrawer: onOpenSddAddress,
              }
            : null;

    const inventoryStatus: DeliveryInventoryStatus | null =
        selectedMethod === 'sth' && !isError
            ? getDeliveryInventoryStatus({
                  method: 'sth',
                  isLoading,
                  availableStock: sth?.availableStock,
                  minimumCaseQty: product.deliveryMethods.STH.purchaseQuantities?.min ?? 1,
                  customPharmacyOOS: Boolean(product.pharmacy?.oos),
              })
            : null;

    return {
        fulfillmentMethods,
        register: register('fulfilmentMethod'),
        sddDetails,
        inventoryStatus,
    };
}
